(ns io.daskul.webapp.widgets.form.lookup-logic
  "Pure dependent-field and URL-building logic for the lookup widget (#792).

   :depends-on accepts a single {:field :filter-param} map (legacy) or a
   vector of them — the session pickers filter by the Lead's program-id AND
   branch-id. Keys/values arrive as strings after the JSON round-trip.
   No React imports so it runs under the :node-test build."
  (:require [clojure.string :as str]))

(defn- normalize-field
  "A :field is a flat key or a path into the record: \"program-id\" or
   [\"active-trial-session\" \"session-id\"]. Always returns a path vector."
  [field]
  (if (sequential? field)
    (mapv keyword field)
    [(keyword field)]))

(defn normalize-depends-on
  "Normalize a :depends-on ui config to a vector of
   {:field [kw...] :filter-param kw :optional bool}.

   :filter-param defaults to the LAST segment of :field.

   :optional marks a filter that refines the options without gating them —
   it contributes its param when it has a value and is simply absent when it
   does not, rather than disabling the field. A required entry says \"these
   options are meaningless until you tell me X\"; an optional one says
   \"...and also leave out Y, if there is a Y\" (#792).

   Returns nil for nil/empty config."
  [depends-on]
  (when depends-on
    (->> (if (map? depends-on) [depends-on] depends-on)
         (keep (fn [{:keys [field filter-param optional]}]
                 (when field
                   (let [path (normalize-field field)]
                     {:field path
                      :filter-param (keyword (or filter-param (last path)))
                      :optional (boolean optional)}))))
         (vec)
         (not-empty))))

(defn dependent-state
  "Resolve normalized depends-on entries against form-data. Returns
   {:missing   [field-kw ...]              ;; REQUIRED entries with no value yet
    :params    [\"filter-param=value\" ...] ;; query params for present values
    :signature \"...\"}                     ;; stable per-render change marker
   or nil when depends-on is nil/empty."
  [depends-on form-data]
  (when (seq depends-on)
    (let [values (mapv #(get-in form-data (:field %)) depends-on)]
      {:missing (vec (keep-indexed
                      (fn [i d] (when (and (not (:optional d)) (nil? (nth values i)))
                                  (last (:field d))))
                      depends-on))
       :params (vec (keep-indexed
                     (fn [i d]
                       (when-some [v (nth values i)]
                         (str (name (:filter-param d)) "="
                              (js/encodeURIComponent (str v)))))
                     depends-on))
       ;; pr-str is unambiguous — a plain join could collide when a value
       ;; contains the separator, reading two changed values as unchanged.
       :signature (pr-str values)})))

(defn build-lookup-url
  "Assemble the lookup request URL from every filter source, in order:
   :default-filters (map), :dependent-params (from dependent-state),
   :value-field, then the autocomplete :query.

   Both the initial load and the debounced search build their URL here so the
   two can never disagree about which filters apply — searching used to drop
   the :depends-on params and quietly widen to the whole table (#799).

   Returns nil when base-url is nil."
  [base-url {:keys [default-filters dependent-params value-field query]}]
  (when base-url
    (let [params (cond-> (mapv (fn [[k v]]
                                 (str (name k) "=" (js/encodeURIComponent (str v))))
                               default-filters)
                   (seq dependent-params) (into dependent-params)
                   value-field (conj (str "value-field=" (name value-field)))
                   (seq query) (conj (str "q=" (js/encodeURIComponent query))))]
      (if (seq params)
        (str base-url "?" (str/join "&" params))
        base-url))))

(defn missing-label
  "Human-readable list of missing dependent fields for the disabled-state
   message: [:program-id :branch-id] → \"program-id and branch-id\"."
  [missing]
  (str/join " and " (map name missing)))
